import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { FormField, Input } from '../../components/ui/FormField.js';
import { ThemeToggle } from '../../components/ui/ThemeToggle.js';
import { useAuth } from '../../context/AuthContext.js';
import { ApiError, api } from '../../services/api.js';
import {
  PASSWORD_INVALID_MESSAGE,
  PASSWORD_RULE_TEXT,
  satisfiesPasswordPolicy,
} from '../../utils/passwordPolicy.js';

/**
 * 035 (R2/FR-003): a user created with a temporary password is force-flagged and
 * blocked from every route except the change-password one. The admin app routes
 * them here on sign-in. Completing the change (the allowlisted
 * PATCH /users/me/password) clears the flag; refreshUser then hydrates the now
 * unblocked session.
 */
export function ForcePasswordChangePage() {
  const { refreshUser, logout } = useAuth();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState('');

  const change = useMutation({
    mutationFn: () =>
      api.patch('/users/me/password', {
        current_password: currentPw,
        new_password: newPw,
        confirm_password: confirmPw,
      }),
    onSuccess: async () => {
      setError('');
      await refreshUser();
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError || err instanceof Error ? err.message : 'Failed to change password',
      );
    },
  });

  function onSubmit() {
    if (!currentPw || !newPw || !confirmPw) {
      setError('All fields are required.');
      return;
    }
    if (newPw !== confirmPw) {
      setError('New passwords do not match.');
      return;
    }
    // 140 US3: the server has enforced this rule on this endpoint since 035, and this screen already
    // STATED it — but nothing stopped a doomed request, and the resulting refusal surfaces as the bare
    // word "ValidationError" (the API client reads `error`; the readable text sits in `message`).
    if (!satisfiesPasswordPolicy(newPw)) {
      setError(PASSWORD_INVALID_MESSAGE);
      return;
    }
    change.mutate();
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-sm bg-canvas border border-hairline rounded-xl p-6">
        <h1 className="text-lg font-semibold text-ink mb-1">Change your password</h1>
        <p className="text-sm text-steel mb-4">
          Your account was created with a temporary password. Set a new password to continue.
        </p>

        <div className="grid gap-3">
          <FormField label="Temporary (current) password" name="fpc_current">
            <Input
              id="fpc_current"
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.currentTarget.value)}
            />
          </FormField>
          <FormField label="New password" name="fpc_new" hint={PASSWORD_RULE_TEXT}>
            <Input
              id="fpc_new"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.currentTarget.value)}
            />
          </FormField>
          <FormField label="Confirm new password" name="fpc_confirm">
            <Input
              id="fpc_confirm"
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.currentTarget.value)}
            />
          </FormField>

          <Button loading={change.isPending} onClick={onSubmit}>
            Set new password
          </Button>
          <Button variant="ghost" size="sm" onClick={logout}>
            Sign out
          </Button>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      </div>
    </div>
  );
}
